// src/services/sourceDocDocx.js
//
// SourceX — export an assembled source document as a real Microsoft Word (.docx)
// file, styled to match the sponsor's filed source-document format (the AIRTIVITY
// visit source is the reference layout).
//
// This is PRESENTATION ONLY, the exact sibling of sourceDocExport.js (which emits
// printable HTML). It renders one visit's questions as a blank, fillable Word
// document and never touches the template, the assembly, or any clinical data.
//
// Why a real document generator (the `docx` library) instead of converting the
// existing HTML: only a native .docx can give Word's repeating page header/footer,
// a live "Page X of Y" field, and real Word tables — the three things the filed
// format needs that an HTML-to-Word wrapper cannot reproduce faithfully.
//
// `buildSourceDocx()` is pure (returns a docx `Document`), so it is unit-testable
// in Node via `Packer.toBuffer`. The browser-only download helper is kept
// separate so importing the builder never drags in Blob/file-saver.

import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  Header,
  Footer,
  PageNumber,
  PageOrientation,
  BorderStyle,
  WidthType,
  ShadingType,
  AlignmentType,
  TabStopType,
  VerticalAlign,
  TableLayoutType,
  TextDirection,
  HeightRule,
} from "docx";
import {
  isSourceOnlyType,
  columnPrintsBoxes,
  columnIsCheckbox,
  columnChoices,
  cellSelection,
  markStyleFor,
  columnHeaderGroups,
  checkboxIsTicked,
  isCompositeTable,
  tableRowHeightPt,
  columnWidthPercents,
  eventIsLog,
  eventIsCriteria,
  eventIsLandscape,
  emphasisParts,
  criterionLines,
  splitThresholdFor,
} from "./templateSchema";
import { LOG_CLOSING_LABEL } from "./sourceLogTemplates";
import { ELIGIBILITY_CLOSING, CRITERIA_PAGE_TITLE } from "./eligibilityChecklist";

// ---------------------------------------------------------------------------
// Page geometry (letter, matching sourceDocExport.js's @page rules)
// ---------------------------------------------------------------------------
const TWIP = {
  page: { width: 12240, height: 15840 }, // 8.5in x 11in
  margin: { top: 1584, bottom: 1152, left: 792, right: 792, header: 576, footer: 432 },
};
// Usable content width between the left/right margins — the anchor for the
// right-aligned tab stops that place the header columns and the red note, and
// the width every table divides up.
//
// LANDSCAPE swaps the paper, not the margins (which is what the print CSS in
// sourceDocExport does too, so the two documents keep breaking their lines in
// the same places). A standing log prints landscape because a 10-column paper
// log cannot be written into on a portrait page.
const CONTENT_W = TWIP.page.width - TWIP.margin.left - TWIP.margin.right; // 10656
const CONTENT_W_LANDSCAPE =
  TWIP.page.height - TWIP.margin.left - TWIP.margin.right; // 14256
const contentWidth = (landscape) =>
  landscape ? CONTENT_W_LANDSCAPE : CONTENT_W;

const SHADE = "D9D9D9"; // section-box fill, matches the grey boxed titles
const RED = "C00000"; // the sponsor's red instruction text ("Prior to other testing")
const HAIR = "999999";
const INK = "111111";

// A fillable blank — a run of underscores sized for the field. Word prints these
// as the write-in line, the document equivalent of an HTML bottom-border span.
const blank = (n = 16) => new TextRun({ text: "_".repeat(n) });
const hint = (t) =>
  new TextRun({ text: ` ${t}`, italics: true, size: 15, color: "888888" });

const CHECK = "☐"; // ☐ empty ballot box
const CHECKED = "☑"; // ☑ ticked ballot box — a recorded answer
const RADIO = "○"; // ○ empty circle — a pick-ONE choice
const RADIO_ON = "●"; // ● filled circle — the one that was picked

/** The mark before a choice: square for tick-any, round for pick-one. Which
 * shape a type gets is decided once, in templateSchema's choiceMarkFor, so
 * Word, the HTML export and the editor preview can't drift apart. */
const markGlyph = (checked, style) =>
  style === "radio" ? (checked ? RADIO_ON : RADIO) : checked ? CHECKED : CHECK;

const choiceRuns = (labels, selected = null, style = "box", stack = false) => {
  const isSel = (l) => Array.isArray(selected) && selected.includes(l);
  return labels.flatMap((label, i) => [
    // Stacked one per line when the column asks for it — a hard break rather
    // than a run of spaces, so a narrow cell can't wrap them somewhere else.
    ...(i ? [new TextRun(stack ? { break: 1 } : { text: "   " })] : []),
    new TextRun({ text: `${markGlyph(isSel(label), style)} ` }),
    new TextRun({ text: String(label), bold: isSel(label) }),
  ]);
};

// One continuation line of a Long text field. Its own paragraph, and kept with
// the label so the block never splits across a page break.
const longTextLine = () =>
  new Paragraph({
    spacing: { before: 0, after: 20 },
    keepLines: true,
    children: [blank(96)],
  });

// A recorded free-text/number/date answer, written on the line (bold so it
// reads as filled-in rather than a printed label).
const answerText = (t) => new TextRun({ text: String(t), bold: true });

/**
 * The runs inside one table cell. A choice or tick-box column prints its boxes,
 * so the blank Word form looks like the paper CRF; every other column keeps the
 * plain-text behaviour it has always had. `prefill` is a coordinator-set
 * default for a blank form — a patient's actual answer always wins over it. For
 * a choice/tick-box column, a default PRE-CHECKS that box rather than replacing
 * the boxes with text — the boxes are still the real answer choices, one is
 * just ticked to start.
 */
const tableCellRuns = (c, raw, filling, prefill) => {
  if (columnIsCheckbox(c)) {
    const on = filling ? raw === true || raw === "true" : !!prefill;
    return [new TextRun({ text: markGlyph(on, "box") })];
  }
  if (columnPrintsBoxes(c)) {
    // cellSelection handles a MultiSelect default with several pre-checked
    // boxes (an array) the same way it handles a single-value default.
    const selected = filling ? cellSelection(raw) : cellSelection(prefill);
    return choiceRuns(columnChoices(c), selected, markStyleFor(c), c.stack === true);
  }
  // A STRUCTURAL column's prefill is printed FORM TEXT, not a pre-entered
  // answer, so it stays in ordinary weight and it prints on the patient's copy
  // too — see makeColumn. Mirrors tableCellHtml.
  if (c.structural) {
    const text = Array.isArray(prefill) ? prefill.join(", ") : prefill;
    return text === undefined || text === null || text === ""
      ? []
      : [new TextRun({ text: String(text) })];
  }
  if (!filling && prefill) {
    const text = Array.isArray(prefill) ? prefill.join(", ") : prefill;
    return [new TextRun({ text, bold: true })];
  }
  const text = Array.isArray(raw) ? raw.join(", ") : raw;
  return text === undefined || text === null || text === ""
    ? []
    : [new TextRun({ text: String(text) })];
};

/**
 * The PARAGRAPHS of one table cell.
 *
 * Almost every cell is a single paragraph of runs, and takes the path it always
 * has. The exception is a structural cell whose criterion carries sub-parts:
 * Word can only indent a whole paragraph, so each line becomes its own
 * paragraph and the sub-parts get a hanging indent — which is how the protocol
 * sets them out, and what a monitor's eye follows when checking the form
 * against it. See criterionLines; mirrors the .crit-sub rule in sourceDocExport.
 */
// 280 twips = 14pt, the same indent the printed page uses (20 twips to a point).
const SUBPART_INDENT_TWIPS = 280;

const tableCellParagraphs = (c, raw, filling, prefill) => {
  if (c.structural) {
    const text = Array.isArray(prefill) ? prefill.join(", ") : prefill;
    const lines = criterionLines(text);
    if (lines) {
      return lines.map((l) =>
        new Paragraph({
          spacing: { before: 0, after: 20 },
          ...(l.indent ? { indent: { left: SUBPART_INDENT_TWIPS } } : {}),
          children: [new TextRun({ text: l.text })],
        })
      );
    }
  }
  return [
    new Paragraph({
      // A single tick box sits in the middle of its narrow column.
      ...(columnIsCheckbox(c) ? { alignment: AlignmentType.CENTER } : {}),
      children: tableCellRuns(c, raw, filling, prefill),
    }),
  ];
};

const hasAnswer = (v) =>
  v !== undefined &&
  v !== null &&
  v !== "" &&
  !(Array.isArray(v) && v.length === 0);

// Every visible border of a box, one width.
const boxBorder = (color = INK, size = 4) => {
  const side = { style: BorderStyle.SINGLE, size, color };
  return { top: side, bottom: side, left: side, right: side };
};

// ---------------------------------------------------------------------------
// Conditional ("skip logic") → a printed "only if …" note. A paper/Word source
// cannot branch, so a conditional field is shown with its condition spelled out —
// identical wording to sourceDocExport.js so the two exports never diverge.
// ---------------------------------------------------------------------------
const conditionText = (cond, labelById) => {
  if (!cond || typeof cond !== "object") return "";
  const join = (arr, sep) =>
    arr.map((c) => conditionText(c, labelById)).filter(Boolean).join(sep);
  if (Array.isArray(cond.all)) return join(cond.all, " and ");
  if (Array.isArray(cond.any)) return join(cond.any, " or ");
  if (cond.not) {
    const inner = conditionText(cond.not, labelById);
    return inner ? `not (${inner})` : "";
  }
  if (cond.questionId) {
    const lbl = labelById.get(cond.questionId) || "a previous answer";
    const op =
      { eq: "=", neq: "≠", gt: ">", gte: "≥", lt: "<", lte: "≤" }[cond.op] || cond.op;
    const val = Array.isArray(cond.value) ? cond.value.join(", ") : cond.value;
    if (cond.op === "isBlank") return `${lbl} is blank`;
    if (cond.op === "notBlank") return `${lbl} is answered`;
    return `${lbl} ${op} ${val}`;
  }
  return "";
};

// ---------------------------------------------------------------------------
// Answer areas, one per field type — the runs that follow a question's label.
// Mirrors answerArea() in sourceDocExport.js.
// ---------------------------------------------------------------------------
const answerRuns = (q, withAnswers = false) => {
  const opts = Array.isArray(q.options) ? q.options : [];
  const v = q.value;
  // A coordinator-set default for the BLANK form — never a patient's actual
  // answer, which always wins once real answers are printing. Same idea as a
  // Table's prefillRows, just for a single (non-Table) question.
  const def = !withAnswers ? cellSelection(q.defaultValue) : [];
  const sel = (chosen) => (withAnswers ? chosen : def);
  const written = (blankLen) => {
    if (withAnswers && hasAnswer(v)) return [answerText(v)];
    if (!withAnswers && q.defaultValue) {
      return [answerText(Array.isArray(q.defaultValue) ? q.defaultValue.join(", ") : q.defaultValue)];
    }
    return [blank(blankLen)];
  };
  switch (q.type) {
    case "YesNo/Maybe":
      return choiceRuns(["Yes", "No", "N/A"], sel(hasAnswer(v) ? [v] : []));
    case "Checkbox": {
      // ONE box, no labels. It used to print "☐ Yes  ☐ No", which is a
      // different question — a checkbox is ticked or it is not, and offering a
      // second box to say "no" invites both being left blank and neither
      // meaning anything. A Yes/No/N/A field is its own type for that.
      // checkboxIsTicked, not a bare === true: a pre-check saved while this
      // printed as Yes/No reads "Yes", and that box still has to come out
      // ticked. See templateSchema.
      const ticked = checkboxIsTicked(withAnswers ? v : q.defaultValue);
      return [new TextRun({ text: markGlyph(ticked, "box") })];
    }
    case "Select":
      // Round marks — a dropdown is pick-ONE. See choiceMarkFor. A question
      // can override that where the printed form it replaces draws boxes.
      return choiceRuns(
        opts.length ? opts : ["Yes", "No"],
        sel(hasAnswer(v) ? [v] : []),
        markStyleFor(q)
      );
    case "MultiSelect":
      return [
        hint("(check all that apply)"),
        new TextRun({ text: "  " }),
        ...choiceRuns(opts, sel(Array.isArray(v) ? v : [])),
      ];
    case "Number":
      return [
        ...written(8),
        ...(q.unit ? [new TextRun({ text: ` ${q.unit}`, color: "555555" })] : []),
      ];
    case "Date":
      return [...written(14), hint("(DD-Mon-YYYY)")];
    case "Time":
      return [...written(8), hint("(HH:MM)")];
    case "DateTime":
      return [...written(20), hint("(DD-Mon-YYYY HH:MM)")];
    case "TextArea":
      // One line here; the two further ones are separate PARAGRAPHS, added in
      // questionElements — Word cannot break a line inside a paragraph on
      // demand, and a run of 200 underscores would wrap wherever it happened
      // to run out of margin rather than at the end of a writing line.
      return written(60);
    // Reached only from a COMPOSITE piece. A standalone Signature question is
    // intercepted in questionElements and printed as its own block (role,
    // attestation, full-width rules) — that block cannot sit inline after a
    // piece label, so a signature bundled in with other answers prints as the
    // signature + date pair the block ends with. Mirrors the Signature case in
    // sourceDocExport's answerArea.
    case "Signature":
      return [
        blank(24),
        hint("Signature"),
        new TextRun({ text: "   " }),
        blank(12),
        hint("Date"),
      ];
    default:
      return written(30);
  }
};

// A shaded, boxed section title (a `Header`-type question), with the sponsor's
// optional red instruction pushed to the right edge ("Prior to other testing").
const sectionTitleParagraph = (q, contentW = CONTENT_W) => {
  const runs = [
    new TextRun({ text: (q.question || "Section").toUpperCase(), bold: true, size: 22 }),
  ];
  if (q.instruction) {
    runs.push(new TextRun({ text: "\t" }));
    runs.push(new TextRun({ text: String(q.instruction), bold: true, color: RED, size: 20 }));
  }
  return new Paragraph({
    children: runs,
    tabStops: [{ type: TabStopType.RIGHT, position: contentW }],
    shading: { type: ShadingType.CLEAR, color: "auto", fill: SHADE },
    border: boxBorder(INK, 4),
    spacing: { before: 220, after: 80 },
    // A section title stranded alone at the foot of a page labels nothing —
    // it has to arrive with at least the start of its section. Mirrors the
    // preview's `break-after: avoid` on h3.section.
    keepNext: true,
  });
};

// A label paragraph: bold text, a red required "*", and an italic "only if …".
// `keepNext` is set when something inseparable follows — a table's title has to
// arrive on the same page as its table.
const labelParagraph = (q, labelById, extraRuns = [], { keepNext = false } = {}) => {
  const runs = [new TextRun({ text: q.question || "", bold: true })];
  if (q.required) runs.push(new TextRun({ text: " *", bold: true, color: RED }));
  const cond = conditionText(q.visibleWhen, labelById);
  if (cond) {
    runs.push(
      new TextRun({ text: `   only if ${cond}`, italics: true, color: "555555", size: 18 })
    );
  }
  return new Paragraph({
    children: [...runs, ...extraRuns],
    spacing: { before: 40, after: 20 },
    border: {
      bottom: { style: BorderStyle.DOTTED, size: 2, color: "DDDDDD" },
    },
    // One field is one row of the form; splitting its lines over a page break
    // is the same defect `break-inside: avoid` prevents in the preview.
    keepLines: true,
    ...(keepNext ? { keepNext: true } : {}),
  });
};

// A signature block: "<Role> Signature: ____  Date: ____" then an optional
// attestation line above it.
const signatureParagraphs = (q) => {
  const role = q.signatureRole || "PI / Sub-I";
  const out = [];
  if (q.attestation) {
    out.push(
      new Paragraph({
        children: [new TextRun({ text: q.attestation, italics: true, size: 18 })],
        spacing: { before: 120, after: 20 },
      })
    );
  }
  out.push(
    new Paragraph({
      spacing: { before: 120, after: 20 },
      children: [
        new TextRun({ text: `${role} Signature: `, bold: true }),
        blank(34),
        new TextRun({ text: "   Date: ", bold: true }),
        blank(16),
      ],
    })
  );
  return out;
};

// Below this per-column width a horizontal header can't fit its words, so a
// single table falls back to rotated vertical headers (bottom-to-top). ~0.62in.
const WIDE_COL_TWIPS = 900;
// The narrowest a column is ever allowed to get (~0.25in) — a floor so a log
// with very many fields still renders every column instead of vanishing.
const MIN_COL_TWIPS = 360;
// Past a page-dependent number of columns a table can't be written into, so it
// is SPLIT into stacked sub-tables of at most CHUNK_DATA_COLS columns each,
// linked by a shared row-number (#) column so the same row lines up across
// them. That number is splitThresholdFor in templateSchema, shared with
// sourceDocExport so the two exports cannot disagree about when to split.
const CHUNK_DATA_COLS = 6;
const INDEX_COL_TWIPS = 520;
const TABLE_BORDER = { style: BorderStyle.SINGLE, size: 4, color: "444444" };

// `extra` carries a spanning header's columnSpan / rowSpan — see the grouped
// header rows in buildTable.
const shadedHeaderCell = (children, width, wide, extra = {}) =>
  new TableCell({
    width: { size: width, type: WidthType.DXA },
    shading: { type: ShadingType.CLEAR, color: "auto", fill: "EEEEEE" },
    verticalAlign: VerticalAlign.CENTER,
    ...(wide ? { textDirection: TextDirection.BOTTOM_TO_TOP_LEFT_TO_RIGHT } : {}),
    ...extra,
    children: [
      new Paragraph({ alignment: AlignmentType.CENTER, children }),
    ],
  });

// A small italic caption above a (sub-)table. Held to the table it introduces —
// "Columns 7–12 (continued)" at the foot of a page, with its sub-table
// overleaf, names nothing.
const caption = (text) =>
  new Paragraph({
    spacing: { before: 100, after: 20 },
    children: [new TextRun({ text, italics: true, size: 16, color: "555555" })],
    keepNext: true,
    keepLines: true,
  });

// Build one real Word table for a set of columns. `dataW` fixes the data-column
// width (so stacked chunks share a width); when omitted it fills the page. With
// `withIndex`, a leading numbered "#" column is added for cross-table row links.
const buildTable = (
  cols,
  {
    withIndex = false,
    minRows,
    maxRows,
    dataW,
    dataRows = null,
    // Fixed row labels (a per-system physical exam, a set lab panel) print in
    // a leading column, same as sourceDocExport.js's HTML table — the row
    // count and labels are part of the FORM, not something Word improvises.
    // `prefillRows` is the coordinator-set default per row/column, mirrored
    // from that same module.
    rowLabels = null,
    rowLabelHeader = "",
    // The share of the page a row-LABEL column takes, when the table names one.
    // See rowLabelWidthPct in makeQuestion: a label column normally holds words
    // and takes a full share, which is the default and stays the default; the
    // eligibility checklist's holds a number.
    rowLabelWidthPct = null,
    prefillRows = null,
    // Sub-headings inside the table, keyed by the body row they precede. One
    // bold row spanning every column. Mirrors gridHtml's groupRow.
    groupRows = null,
    // Explicit per-column shares (a paper log, whose columns are nothing like
    // equal) and lines of writing space per row. Both absent on every table
    // that predates logs, which keeps their equal columns and single-line rows.
    widthPercents = null,
    rowHeightPt = 16,
    headerFontPt = 9,
    contentW = CONTENT_W,
    indexHeader = "#",
    // A log's own "No." column prints EMPTY — the paper form leaves the
    // coordinator to number their entries. The SPLIT path still prints 1..N,
    // because there the number is the only thing tying one row across the
    // stacked sub-tables.
    blankIndex = false,
  } = {}
) => {
  const hasRowLabels = Array.isArray(rowLabels) && rowLabels.length > 0;
  const withIndex_ = withIndex || hasRowLabels;

  // The leading column is one of two completely different things, and sizing
  // them the same is why a row-labelled table came out unreadable:
  //
  //   "#"  — a cross-reference NUMBER tying the chunks of a split wide log
  //          together. Two or three digits; INDEX_COL_TWIPS is right for it.
  //   label — a row NAME ("Cardiovascular", "Gastrointestinal"). Words, and
  //          they were being squeezed into that same 0.36in.
  //
  // A label column therefore takes a full share, exactly like every other
  // column — which is also what the preview does, since a fixed-layout HTML
  // table divides itself evenly across all of its columns including that one.
  const labelIsText = hasRowLabels;
  // …unless the table has named the share it wants, which is the checklist's
  // "#" column saying it holds two digits, not a word.
  const labelPct = Number(rowLabelWidthPct);
  const sizedLabel = labelIsText && Number.isFinite(labelPct) && labelPct > 0;
  const sizedLabelW = sizedLabel
    ? Math.max(Math.floor((contentW * labelPct) / 100), MIN_COL_TWIPS)
    : 0;

  const shares = cols.length + (labelIsText && !sizedLabel ? 1 : 0);
  const reserved = sizedLabel
    ? sizedLabelW
    : labelIsText
    ? 0
    : withIndex_
    ? INDEX_COL_TWIPS
    : 0;
  const colW =
    dataW || Math.max(Math.floor((contentW - reserved) / shares), MIN_COL_TWIPS);
  const indexW = sizedLabel
    ? sizedLabelW
    : withIndex_
    ? labelIsText
      ? colW
      : INDEX_COL_TWIPS
    : 0;

  // Explicit shares, when the table carries them. They divide what is left
  // after the lead column, exactly as the preview's <colgroup> percentages do
  // once it has taken INDEX_COL_IN off the top. Without them every column
  // takes the same `colW` it always has.
  const dataW_ = colW * cols.length;
  const sized =
    Array.isArray(widthPercents) && widthPercents.length === cols.length
      ? widthPercents.map((p) =>
          Math.max(Math.floor((dataW_ * p) / 100), MIN_COL_TWIPS)
        )
      : null;
  const widthOf = (i) => (sized ? sized[i] : colW);

  // Rotating the header labels to read bottom-to-top is a LAST RESORT for a
  // table whose columns we had to guess at: it keeps a narrow column legible,
  // at the cost of a header band up to 2.2in tall and text nobody can read
  // left to right.
  //
  // A table with explicit widths is not that table. Its layout came off a real
  // form, whose own headers sit horizontal at a small size, so rotating here
  // would both look wrong and — because `headerHeight` scales with the longest
  // label — eat the vertical room the log needs to fit one page. It is also
  // what made the two exports disagree: the HTML side has never rotated.
  const narrowest = sized ? Math.min(...sized) : colW;
  const wide = !sized && narrowest < WIDE_COL_TWIPS;

  // Word's autofit collapses columns to their narrowest content (one character
  // per line) unless the table declares an explicit grid AND a fixed layout.
  // `columnWidths` emits <w:tblGrid>; TableLayoutType.FIXED makes Word honour it.
  const columnWidths = [
    ...(withIndex_ ? [indexW] : []),
    ...cols.map((_, i) => widthOf(i)),
  ];
  const tableW = columnWidths.reduce((a, b) => a + b, 0);

  // For rotated headers, the row must be tall enough for the longest label to
  // read down the column. Size it from that label's length, clamped.
  const longest = cols.reduce(
    (m, c) => Math.max(m, String(c.label || "").length + (c.unit ? String(c.unit).length + 3 : 0)),
    0
  );
  const headerHeight = wide ? Math.min(Math.max(longest * 120 + 300, 1400), 3200) : 0;

  // Header type size, in half-points. A standing log's headers carry the only
  // instructions on the page, so they print larger than the 9pt a visit's
  // inline grid uses. Mirrors --head-f in sourceDocExport's CSS.
  const headSize = wide ? 16 : Math.round((headerFontPt || 9) * 2);
  const subSize = Math.max(headSize - 4, 12);

  // One column's header runs: its name, then any sublabel on its OWN line,
  // smaller and not bold — "(DD-MMM-YYYY)" under "End Date". A break rather
  // than a space, so it is a second line however wide the column is.
  // A column can mark ONE word of its header to print bold and underlined — the
  // "yes"/"no" that tells the two eligibility grids apart. Three runs instead of
  // one; the split itself is emphasisParts in templateSchema, shared with the
  // HTML export so the two can't mark different words.
  const labelRuns = (c) => {
    const base = { bold: true, size: headSize };
    const parts = emphasisParts(c);
    if (!parts) return [new TextRun({ text: c.label || "", ...base })];
    const [before, hit, after] = parts;
    return [
      ...(before ? [new TextRun({ text: before, ...base })] : []),
      new TextRun({ text: hit, ...base, underline: {} }),
      ...(after ? [new TextRun({ text: after, ...base })] : []),
    ];
  };
  const headRuns = (c) => [
    ...labelRuns(c),
    ...(c.unit
      ? [new TextRun({ text: ` (${c.unit})`, size: subSize, color: "555555" })]
      : []),
    ...(c.sublabel
      ? [
          new TextRun({ break: 1 }),
          new TextRun({ text: c.sublabel, size: subSize, color: "333333" }),
        ]
      : []),
  ];

  const indexHeaderCell = () =>
    shadedHeaderCell(
      [
        new TextRun({
          // "#" ties the chunks of a SPLIT table together; a log's own
          // numbered column is the same column with the form's own
          // heading on it ("No.").
          text: hasRowLabels ? rowLabelHeader || "" : indexHeader,
          bold: true,
          size: headSize,
        }),
      ],
      indexW,
      false
    );

  // Spanning group headers ("Corticoids" over two columns) turn the header into
  // TWO rows: the groups on top, the column names beneath. An ungrouped column
  // spans both rows instead (rowSpan), so its name still sits beside the others
  // rather than floating in the top band.
  const groups = columnHeaderGroups(cols);
  const headerRows = [];
  const headerRowOpts = {
    tableHeader: true,
    cantSplit: true,
    ...(wide ? { height: { value: headerHeight, rule: HeightRule.ATLEAST } } : {}),
  };

  if (groups) {
    const top = [
      ...(withIndex_
        ? [
            shadedHeaderCell(
              [
                new TextRun({
                  text: hasRowLabels ? rowLabelHeader || "" : indexHeader,
                  bold: true,
                  size: headSize,
                }),
              ],
              indexW,
              false,
              { rowSpan: 2 }
            ),
          ]
        : []),
      ...groups.map((g) =>
        g.label
          ? shadedHeaderCell(
              [new TextRun({ text: g.label, bold: true, size: headSize })],
              cols
                .slice(g.from, g.from + g.span)
                .reduce((sum, _, k) => sum + widthOf(g.from + k), 0),
              false,
              { columnSpan: g.span }
            )
          : shadedHeaderCell(headRuns(cols[g.from]), widthOf(g.from), false, {
              rowSpan: 2,
            })
      ),
    ];
    const second = groups
      .filter((g) => g.label)
      .flatMap((g) =>
        cols
          .slice(g.from, g.from + g.span)
          .map((c, k) => shadedHeaderCell(headRuns(c), widthOf(g.from + k), false))
      );
    headerRows.push(new TableRow({ ...headerRowOpts, children: top }));
    headerRows.push(new TableRow({ ...headerRowOpts, children: second }));
  } else {
    const dataHeaderCells = cols.map((c, i) =>
      shadedHeaderCell(headRuns(c), widthOf(i), wide)
    );
    headerRows.push(
      new TableRow({
        ...headerRowOpts,
        children: withIndex_
          ? [indexHeaderCell(), ...dataHeaderCells]
          : dataHeaderCells,
      })
    );
  }

  // Patient export fills one row per recorded entry; a blank form prints empty
  // rows to write into. A fixed-row table's row COUNT is the label list itself,
  // not a computed default — the rows are part of the form.
  //
  // The blank-form count is tablePrintRowCount's, verbatim: minRows exactly as
  // set, and 4 only as the fallback for a table that never had one. It used to
  // floor the count at 4 here and nowhere else, so a 3-row table drew 3 rows in
  // the preview and the HTML export but 4 in Word — a phantom blank row under a
  // table the coordinator had drawn and filled in themselves.
  const filling = Array.isArray(dataRows);
  const nRows = hasRowLabels
    ? rowLabels.length
    : filling
    ? dataRows.length
    : Math.min(
        Number.isInteger(maxRows) ? maxRows : Infinity,
        Number.isInteger(minRows) && minRows > 0 ? minRows : 4
      );
  // Give body rows a little height so there is room to hand-write in each cell.
  // 16pt (320 twips) is one line; a standing log asks for more, because it is
  // filled in with a pen and a drug name plus an indication does not fit on
  // one. 20 twips to the point.
  const bodyRowH = Math.max(1, rowHeightPt) * 20;

  // A sub-heading INSIDE the table — "Medical Conditions:" partway down an
  // exclusion list. One bold, shaded cell spanning every column, drawn
  // immediately BEFORE the body row it is keyed to, so it takes no number of
  // its own and shifts nothing below it. Mirrors gridHtml's groupRow.
  const totalCols = cols.length + (withIndex_ ? 1 : 0);
  const groupRow = (r) =>
    groupRows && groupRows[r]
      ? [
          new TableRow({
            cantSplit: true,
            children: [
              new TableCell({
                columnSpan: totalCols,
                width: { size: tableW, type: WidthType.DXA },
                shading: { type: ShadingType.CLEAR, color: "auto", fill: "F2F2F2" },
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({ text: String(groupRows[r]), bold: true }),
                    ],
                  }),
                ],
              }),
            ],
          }),
        ]
      : [];

  const bodyRows = Array.from({ length: nRows }).flatMap((_, r) => {
    const prefillRow = Array.isArray(prefillRows) ? prefillRows[r] : null;
    return [...groupRow(r), new TableRow({
      height: { value: bodyRowH, rule: HeightRule.ATLEAST },
      // A row is one entry on the form and must land on ONE page. Word will
      // otherwise cut a tall row in half at a page boundary, leaving the top
      // of a med-log line on one page and the rest on the next — which is
      // both unreadable and un-signable. The TABLE still flows across pages
      // (a 50-row log has to), and its header row repeats, so this only stops
      // the one break that produces nonsense. The preview never splits a
      // block either, for the same reason.
      cantSplit: true,
      children: [
        ...(withIndex_
          ? [
              new TableCell({
                width: { size: indexW, type: WidthType.DXA },
                verticalAlign: VerticalAlign.CENTER,
                children: [
                  new Paragraph({
                    children: [
                      new TextRun(
                        hasRowLabels
                          ? { text: String(rowLabels[r] || ""), size: 18 }
                          : {
                              text: blankIndex ? "" : String(r + 1),
                              size: 18,
                              color: "555555",
                            }
                      ),
                    ],
                  }),
                ],
              }),
            ]
          : []),
        ...cols.map((c, i) => {
          const cell = filling && dataRows[r] ? dataRows[r][c.id] : "";
          const prefill = prefillRow ? prefillRow[c.id] : undefined;
          return new TableCell({
            width: { size: widthOf(i), type: WidthType.DXA },
            // Usually one paragraph; several when a criterion's sub-parts have
            // to hang on their own indented lines. A tick box is centred in its
            // narrow column. See tableCellParagraphs.
            children: tableCellParagraphs(c, cell, filling, prefill),
          });
        }),
      ],
    })];
  });

  return new Table({
    width: { size: tableW, type: WidthType.DXA },
    columnWidths,
    layout: TableLayoutType.FIXED,
    borders: {
      top: TABLE_BORDER,
      bottom: TABLE_BORDER,
      left: TABLE_BORDER,
      right: TABLE_BORDER,
      insideHorizontal: TABLE_BORDER,
      insideVertical: TABLE_BORDER,
    },
    // Header rows repeat at the top of every page the table runs onto and are
    // never themselves split — a header cut in half labels nothing. There are
    // TWO of them when the table has spanning column groups.
    rows: [...headerRows, ...bodyRows],
  });
};

// A composite question — several answer pieces bundled under one question via
// the field-type palette — is stored as a real Table (one row, no row
// labels; see isCompositeTable) so every renderer already handles its data,
// but it prints as a stack of labeled lines rather than a one-row grid.
// Each line is rendered by answerRuns, the SAME renderer a standalone question
// of that type uses — not by tableCellRuns. A table cell is a box on a grid, so
// it writes no fill line and no format hint: the grid's ruling is the line and
// the column header carries the hint. A composite has neither, so routing its
// pieces through tableCellRuns printed a bold label followed by literally
// nothing for every write-in type — a Date lost "____ (DD-Mon-YYYY)", a Number
// lost its line, a Long text lost all three of its lines. Choice pieces
// (dropdown, checklist, Yes/No, checkbox) already drew their tick boxes and
// print exactly as they did before. Mirrors compositeTableHtml in
// sourceDocExport, so the two documents stay identical.
//
// `unit` is dropped from the piece because the label already prints it
// ("Weight (kg):") and answerRuns would otherwise repeat it after the line.
const compositePieceQuestion = (c, row, prefillRow) => ({
  ...c,
  unit: "",
  value: row[c.id],
  defaultValue: prefillRow[c.id],
});

const compositeTableElements = (q, withAnswers) => {
  const row = (withAnswers && Array.isArray(q.value) && q.value[0]) || {};
  const prefillRow = (Array.isArray(q.prefillRows) && q.prefillRows[0]) || {};
  return q.columns.flatMap((c) => {
    const piece = compositePieceQuestion(c, row, prefillRow);
    const line = new Paragraph({
      spacing: { before: 40, after: 20 },
      children: [
        new TextRun({
          text: `${c.label || "(untitled)"}${c.unit ? ` (${c.unit})` : ""}: `,
          bold: true,
          size: 18,
        }),
        ...answerRuns(piece, withAnswers),
      ],
    });
    // Long text's two further write-in lines are separate paragraphs in Word —
    // same rule questionElements applies to a standalone Long text field.
    return piece.type === "TextArea" && !(withAnswers && hasAnswer(piece.value))
      ? [line, longTextLine(), longTextLine()]
      : [line];
  });
};

// A repeatable log → the doc elements it contributes (a table, or, for a very
// wide log, a caption + several stacked sub-tables). Returns an ARRAY.
const tablesForQuestion = (q, withAnswers = false, landscape = false) => {
  const cols = Array.isArray(q.columns) ? q.columns : [];
  if (!cols.length) {
    return [new Paragraph({ children: [hint("(table — no columns)")] })];
  }
  if (isCompositeTable(q)) return compositeTableElements(q, withAnswers);
  // Recorded rows for a patient export (null → blank rows to write into).
  const dataRows =
    withAnswers && Array.isArray(q.value) && q.value.length ? q.value : null;
  const rowLabels = Array.isArray(q.rowLabels) ? q.rowLabels : [];
  const prefillRows = Array.isArray(q.prefillRows) ? q.prefillRows : null;
  const contentW = contentWidth(landscape);
  const rowHeightPt = tableRowHeightPt(q);

  // Fits on the page — one table (rotating headers only if still a touch
  // narrow). `numbered` asks for the lead column here too: a paper log's "No."
  // column is the same column the split path below uses to tie its chunks
  // together, just wanted on a table that isn't being split.
  if (cols.length <= splitThresholdFor(landscape)) {
    return [
      buildTable(cols, {
        withIndex: q.numbered === true,
        indexHeader: q.numberHeader || "#",
        blankIndex: true,
        minRows: q.minRows,
        maxRows: q.maxRows,
        dataRows,
        rowLabels: rowLabels.length ? rowLabels : null,
        rowLabelHeader: q.rowLabelHeader || "",
        rowLabelWidthPct: q.rowLabelWidthPct,
        prefillRows,
        groupRows: q.groupRows,
        widthPercents: columnWidthPercents(cols),
        headerFontPt: q.headerFontPt,
        rowHeightPt,
        contentW,
      }),
    ];
  }

  // Too wide to write into: split column-wise into stacked sub-tables. Every
  // chunk uses the SAME data-column width (sized for a full chunk) so columns
  // line up, and carries a numbered # column so a coordinator can follow one
  // row (e.g. AE #3) across all the sub-tables.
  const chunkW = Math.floor((contentW - INDEX_COL_TWIPS) / CHUNK_DATA_COLS);
  const chunks = [];
  for (let i = 0; i < cols.length; i += CHUNK_DATA_COLS) {
    chunks.push(cols.slice(i, i + CHUNK_DATA_COLS));
  }
  const out = [
    caption(
      `This log has ${cols.length} columns — shown as ${chunks.length} tables below. The # column is the same row in each.`
    ),
  ];
  chunks.forEach((chunk, ci) => {
    const from = ci * CHUNK_DATA_COLS + 1;
    const to = ci * CHUNK_DATA_COLS + chunk.length;
    out.push(caption(`Columns ${from}–${to}${ci ? " (continued)" : ""}`));
    out.push(
      buildTable(chunk, {
        withIndex: true,
        minRows: q.minRows,
        maxRows: q.maxRows,
        dataW: chunkW,
        dataRows,
        // Starting values belong to a ROW, and row 3 is row 3 in every chunk —
        // so they print in each sub-table just as they would in one wide one.
        // Without this a wide table silently dropped everything a coordinator
        // had typed into the grid. rowLabels are deliberately NOT passed: the
        // leading column here is the "#" cross-reference that ties the chunks
        // together, which is what makes a split log readable at all.
        prefillRows,
        // A sub-heading belongs to a ROW, so it repeats above the same row in
        // every chunk — the same reasoning as prefillRows above.
        groupRows: q.groupRows,
        // Not widthPercents: a chunk is a slice of the columns, and its
        // shares would be shares of that slice — which is exactly what stops
        // the chunks lining up. Every chunk uses the same `chunkW` instead.
        rowHeightPt,
        contentW,
      })
    );
  });
  return out;
};

// The fixed closing block that ends EVERY source document — the Appointments
// section and the staff/PI signatures that go with it — mirroring the standard
// last page of the filed source format. Appended unconditionally, independent
// of any Signature questions in the template.
//
// It used to open with a second "PI/ Sub PI Signature:" and "Print Name:" pair
// ABOVE the Appointments heading, which asked the investigator to sign the same
// visit twice — once before the appointment block and once inside it. Only the
// Appointments section's own sign-off is wanted. Mirrored in closingBlockHtml.
const closingBlockElements = () => {
  const sigLine = (label, len) =>
    new Paragraph({
      spacing: { before: 140, after: 20 },
      children: [
        new TextRun({ text: `${label} `, bold: true }),
        blank(len),
        new TextRun({ text: "   Date: ", bold: true }),
        blank(18),
      ],
    });

  return [
    // Breathing room between the visit content and the sign-off.
    new Paragraph({ spacing: { before: 240 }, children: [] }),
    sectionTitleParagraph({ question: "Appointments" }),
    new Paragraph({
      spacing: { before: 80, after: 20 },
      children: [
        new TextRun({ text: "Schedule for next appointment, " }),
        blank(4),
        new TextRun({ text: "/" }),
        blank(6),
        new TextRun({ text: "/" }),
        blank(5),
        new TextRun({ text: " at " }),
        blank(5),
        new TextRun({ text: ":" }),
        blank(6),
        new TextRun({ text: "   Phone Visit" }),
      ],
    }),
    sigLine("Staff Signature:", 34),
    sigLine("PI/ Sub PI Signature:", 40),
  ];
};

/**
 * What ends a LOG page instead of the block above.
 *
 * A log is signed off ONCE, when the study ends — not per visit, and it has no
 * next appointment to schedule. Printing the visit closing block on it would
 * ask for four signatures on a page that wants one. Mirrors logClosingHtml in
 * sourceDocExport.
 */
const logClosingElements = () => [
  new Paragraph({ spacing: { before: 240 }, children: [] }),
  new Paragraph({
    spacing: { before: 140, after: 20 },
    children: [
      new TextRun({ text: `${LOG_CLOSING_LABEL} `, bold: true }),
      blank(38),
      new TextRun({ text: "   Date", bold: true }),
      blank(5),
      new TextRun({ text: "/" }),
      blank(6),
      new TextRun({ text: "/" }),
      blank(7),
    ],
  }),
];

/**
 * What ends the ELIGIBILITY CHECKLIST — the initial screening review and the
 * final eligibility review, each with its attestation, its Yes/No and its two
 * signatures. Printed unconditionally, as the visit closing block is.
 *
 * The wording comes from ELIGIBILITY_CLOSING so this and criteriaClosingHtml
 * render the same sentences — an attestation is what someone is signing, and
 * the two documents saying it differently would be two different attestations.
 */
// Spacing is in TWENTIETHS OF A POINT (docx `spacing`), so 360 = 18pt. These
// mirror the .crit-closing rules in sourceDocExport: a signature is written by
// hand on a printed page, so the line needs room around it, not just a rule to
// sit on.
//
// keepNext on EVERY paragraph but the last chains the whole block together, so
// Word moves the entire sign-off to a new page rather than splitting it. A
// signature means nothing apart from the attestation above it, and half a
// sign-off at the foot of a page is exactly the thing a monitor queries.
const criteriaClosingElements = () => {
  const blocks = ELIGIBILITY_CLOSING.map((b, i) => {
    const keepNext = i < ELIGIBILITY_CLOSING.length - 1;
    if (b.kind === "heading") {
      return new Paragraph({
        spacing: { before: 400, after: 80 },
        keepNext,
        keepLines: true,
        children: [new TextRun({ text: b.text, bold: true, size: 22 })],
      });
    }
    if (b.kind === "attestation") {
      return new Paragraph({
        spacing: { before: 80, after: 200 },
        keepNext,
        keepLines: true,
        children: [new TextRun({ text: b.text, italics: true, size: 18 })],
      });
    }
    if (b.kind === "question") {
      return new Paragraph({
        spacing: { before: 200, after: 240 },
        keepNext,
        keepLines: true,
        children: [
          new TextRun({ text: `${b.text}   `, bold: true }),
          ...choiceRuns(b.options || [], null, "box"),
        ],
      });
    }
    return new Paragraph({
      spacing: { before: 360, after: 80 },
      keepNext,
      keepLines: true,
      children: [
        new TextRun({ text: `${b.label} `, bold: true }),
        blank(34),
        new TextRun({ text: "   Date: ", bold: true }),
        blank(18),
      ],
    });
  });
  // A little air between the last criteria grid and the sign-off, so the block
  // does not butt straight up against the table.
  return [new Paragraph({ spacing: { before: 240 }, keepNext: true, children: [] }), ...blocks];
};

// One question → the paragraphs/table it contributes to the body.
/**
 * Should this question print without its own label?
 *
 * On a LOG, when the label is exactly the log's name: the centred bold title at
 * the top of the page already says it, so printing it again puts the same words
 * twice in a row under the heading.
 *
 * On the CHECKLIST, when a grid's title is already its own Criteria column
 * header — which is the point, since a table header repeats at the top of every
 * page the grid runs onto.
 *
 * The label still has to EXIST — validateTemplate rejects a question with no
 * text — it just doesn't print. Nothing about a visit's rendering changes.
 * Mirrors labelIsPageTitle in sourceDocExport.
 */
const labelIsPageTitle = (q, { isLog, isCriteria, eventName }) => {
  const label = String(q.question || "").trim();
  if (!label) return false;
  if (isLog && eventName) return label === String(eventName).trim();
  if (isCriteria) {
    const first = Array.isArray(q.columns) ? q.columns[0] : null;
    return !!first && String(first.label || "").trim() === label;
  }
  return false;
};

const questionElements = (q, labelById, withAnswers = false, layout = {}) => {
  const { landscape = false, contentW = CONTENT_W } = layout;
  if (q.type === "Header") return [sectionTitleParagraph(q, contentW)];
  if (q.type === "Signature") return signatureParagraphs(q);
  if (q.type === "Table") {
    return [
      // On a log, a grid that follows a question needs a line of air between
      // the two. A Word table takes no margin of its own, so the gap has to be
      // an empty paragraph — 240 twips is one line of the 10pt body type,
      // matching `.q.table-q.spaced` in sourceDocExport. Only when something
      // precedes it: the log whose table is the first thing on the page must
      // not be pushed down, since its row count is sized to the page.
      ...(layout.spacedTable
        ? [new Paragraph({ spacing: { after: 240 }, children: [] })]
        : []),
      ...(labelIsPageTitle(q, layout)
        ? []
        : [labelParagraph(q, labelById, [], { keepNext: true })]),
      ...tablesForQuestion(q, withAnswers, landscape),
    ];
  }
  // Inline answer types (checkboxes, blanks) sit on the same line as the label;
  // TextArea and the fall-through get the answer on the label line too, which
  // keeps one row per field exactly like the reference source.
  const label = labelParagraph(q, labelById, [
    new TextRun({ text: "   " }),
    ...answerRuns(q, withAnswers),
  ]);

  // Long text gets two further write-in lines beneath the first. Against Text
  // it was only 60 underscores versus 30 — a difference nobody spots on a
  // printed page, so the two fields came out looking the same. The extra lines
  // are what actually says "there is room to write here". Not printed once a
  // real answer is on the page: blank lines under a written answer are space
  // for someone to add to a signed record.
  if (q.type === "TextArea" && !(withAnswers && hasAnswer(q.value))) {
    return [label, longTextLine(), longTextLine()];
  }
  return [label];
};

// ---------------------------------------------------------------------------
// Repeating header & footer
// ---------------------------------------------------------------------------
// Separator between centered header/footer fields (a few spaces read cleaner
// than a punctuation divider when the whole line is centered).
const SEP = "      ";

// Whether the header names a visit, and whether the footer counts pages, are
// TWO independent decisions that happened to agree for as long as a log was the
// only thing that wasn't a visit. The eligibility checklist splits them: it
// belongs to the study rather than to a visit (so no "Visit:", no "Visit Date:")
// but it is a finished document of known length (so a live "Page 2 of 3").
// Mirrors namesAVisit / countsPages in sourceDocExport.
const namesAVisit = ({ isLog, isCriteria }) => !isLog && !isCriteria;
const countsPages = ({ isLog }) => !isLog;
// A visit's footer date identifies the approved document a monitor checks a
// filed page against. The CHECKLIST is dated by the signature lines on it, so a
// footer date would be a second date to reconcile against those. Mirrors
// showsFooterDate in sourceDocExport.
const showsFooterDate = ({ isCriteria }) => !isCriteria;

/** "BI: 1397-0014 AIRTIVITY". Identical to before when all three are set; a
 *  missing one no longer prints bare punctuation. Mirrors sourceDocExport. */
const studyLineFor = (c) => {
  const study = [c.studyId, c.studyName].map((s) => String(s || "").trim()).filter(Boolean).join(" ");
  const sponsor = String(c.sponsor || "").trim();
  if (sponsor && study) return `${sponsor}: ${study}`;
  return sponsor || study;
};

const docHeader = (c, eventName = "", layout = {}) => {
  const studyLine = studyLineFor(c);
  return new Header({
    children: [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        border: { bottom: { style: BorderStyle.SINGLE, size: 12, color: INK } },
        spacing: { after: 40 },
        children: [
          new TextRun({ text: studyLine, bold: true }),
          new TextRun({ text: `${SEP}Site: ${c.site || ""}`, bold: true }),
          new TextRun({ text: `${SEP}PI: ${c.pi || ""}`, bold: true }),
        ],
      }),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        border: { bottom: { style: BorderStyle.SINGLE, size: 4, color: HAIR } },
        spacing: { after: 120 },
        children: [
          // Which visit this is, in the REPEATING header — the centred title
          // below it only appears on page one, so from page two onward a
          // filed-away page had nothing on it saying which visit it belonged
          // to.
          //
          // A LOG and the eligibility CHECKLIST carry neither this nor the visit
          // date below. Each is one standing document whose name is already the
          // heading on it, and neither has a single visit date to write. Both
          // were on them because they reuse the visit's chrome; neither means
          // anything there.
          //
          // The name prints on its own, with no "Visit:" in front of it. The
          // visit's name already says what it is ("Visit 2 (Week 4)",
          // "Screening"), so the label was a word of chrome in a header line
          // that has to stay short — and the CHECKLIST below has always named
          // itself bare, so this is now one rule rather than two.
          ...(namesAVisit(layout) && eventName
            ? [new TextRun({ text: `${eventName}${SEP}`, bold: true })]
            : // The CHECKLIST names itself in the repeating header, the same
              // way a visit does — the centred title only prints on page one.
              layout.isCriteria
              ? [new TextRun({ text: `${CRITERIA_PAGE_TITLE}${SEP}`, bold: true })]
              : []),
          new TextRun({ text: `Patient Number: ${c.patientPrefix || ""}` }),
          c.patientNumber ? answerText(c.patientNumber) : blank(6),
          new TextRun({ text: `${SEP}Patient Initials: ` }),
          c.patientInitials ? answerText(c.patientInitials) : blank(6),
          ...(namesAVisit(layout)
            ? [new TextRun({ text: `${SEP}Visit Date: ` }), blank(8)]
            : []),
        ],
      }),
    ],
  });
};

const docFooter = (c, layout = {}) =>
  new Footer({
    children: [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        border: { top: { style: BorderStyle.SINGLE, size: 4, color: HAIR } },
        spacing: { before: 40 },
        children: [
          new TextRun({ text: c.version || "", size: 16, color: "444444" }),
          // The CHECKLIST prints no footer date — it is dated by the signature
          // lines on it. See showsFooterDate; mirrors sourceDocExport.
          ...(c.date && showsFooterDate(layout)
            ? [new TextRun({ text: `${SEP}${c.date}`, size: 16, color: "444444" })]
            : []),
          new TextRun({ text: `${SEP}Page `, size: 16, color: "444444" }),
          // A log is ONGOING: a coordinator photocopies the blank page as many
          // times as the study needs, so a live page count would be wrong on
          // every copy but the first. Literal blanks, filled in by hand. A
          // visit — and the eligibility checklist, which is printed once and
          // filed — is a fixed document and keeps its real "Page X of Y" fields.
          ...(countsPages(layout)
            ? [
                new TextRun({ children: [PageNumber.CURRENT], size: 16, color: "444444" }),
                new TextRun({ text: " of ", size: 16, color: "444444" }),
                new TextRun({ children: [PageNumber.TOTAL_PAGES], size: 16, color: "444444" }),
              ]
            : [
                blank(4),
                new TextRun({ text: " of ", size: 16, color: "444444" }),
                blank(4),
              ]),
        ],
      }),
    ],
  });

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Build a Word document for one visit's source document.
 *
 * @param {object} args
 * @param {object} args.event   one event (visit) of the assembled template
 * @param {object} args.config  { sponsor, studyId, studyName, site, pi, version, date, patientPrefix }
 * @param {boolean} [args.withAnswers]  render recorded answers (patient export)
 *                  instead of a blank form. Defaults to false — a blank form.
 * @returns {import("docx").Document}
 */
export const buildSourceDocx = ({ event, config = {}, withAnswers = false }) => {
  const questions = (event && event.questions) || [];
  const labelById = new Map(questions.map((q) => [q.id, q.question]));

  // A standing log prints differently from a visit in four ways — landscape
  // paper, a blank "Page __ of __", a "Log:" header and a single sign-off line
  // instead of the visit closing block. All four are decided here, from the
  // event, and threaded down; nothing below re-derives them. Mirrors
  // buildSourceDocHtml.
  const eventName = (event && event.eventName) || "";
  const isLog = eventIsLog(event);
  // The eligibility checklist — a visit's live page count and a log's
  // study-level header. See namesAVisit / countsPages.
  const isCriteria = eventIsCriteria(event);
  const landscape = eventIsLandscape(event);
  const contentW = contentWidth(landscape);
  const chrome = { isLog, isCriteria };

  // Same inclusion rule as the HTML export: keep section headers, signatures,
  // and every real (non-source-only) question.
  const body = questions
    .filter(
      (q) =>
        q &&
        (q.type === "Header" || q.type === "Signature" || !isSourceOnlyType(q.type))
    )
    .flatMap((q, i) =>
      questionElements(q, labelById, withAnswers, {
        isLog,
        isCriteria,
        landscape,
        contentW,
        eventName,
        // A standing page's grid gets a line of air above it when something
        // precedes it — see the Table branch in questionElements. On the
        // checklist that is what separates the inclusion grid from the
        // exclusion one.
        spacedTable: (isLog || isCriteria) && q.type === "Table" && i > 0,
      })
    );

  const visitTitle = new Paragraph({
    alignment: AlignmentType.CENTER,
    spacing: { after: 160 },
    children: [
      new TextRun({
        // The checklist's page-one heading is what the document is CALLED —
        // the same name its repeating header carries, not the section name it
        // happens to be filed under in the editor.
        text: isCriteria ? CRITERIA_PAGE_TITLE : eventName,
        bold: true,
        size: 26,
        // On a LOG this line is the page heading the paper form prints in caps
        // — "CONCOMITANT MEDICATION LOG". allCaps rather than storing it
        // shouted, so the log is still named normally in the editor's
        // dropdowns. The checklist's own band is capped for the same reason.
        // Mirrors `body.log .visit-title` in sourceDocExport.
        ...(isLog || isCriteria ? { allCaps: true } : {}),
      }),
    ],
  });

  return new Document({
    styles: {
      default: {
        document: {
          run: { font: "Calibri", size: 20, color: INK }, // 10pt
        },
      },
    },
    sections: [
      {
        properties: {
          page: {
            // Landscape swaps the paper, not the margins — so the two exports
            // keep breaking their lines in the same places.
            //
            // The width/height stay PORTRAIT here: docx swaps them itself when
            // the orientation is landscape, so handing it pre-swapped values
            // swaps them straight back and emits an 8.5in-wide page flagged
            // landscape — which is not a landscape page.
            size: {
              width: TWIP.page.width,
              height: TWIP.page.height,
              ...(landscape ? { orientation: PageOrientation.LANDSCAPE } : {}),
            },
            margin: TWIP.margin,
          },
        },
        headers: { default: docHeader(config, eventName, chrome) },
        footers: { default: docFooter(config, chrome) },
        children: [
          visitTitle,
          ...body,
          ...(isLog
            ? logClosingElements()
            : isCriteria
            ? criteriaClosingElements()
            : closingBlockElements()),
        ],
      },
    ],
  });
};

/** A safe .docx filename from the study id + visit name. */
export const sourceDocxFilename = (event, config = {}) => {
  const base = [config.studyId, event && event.eventName]
    .filter(Boolean)
    .join(" ")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "_")
    .slice(0, 110);
  return base ? `${base}_source.docx` : "source_document.docx";
};

/**
 * Browser-only: build the document, pack it to a Blob, and trigger a download.
 * Kept out of buildSourceDocx so the pure builder stays importable in tests
 * without pulling in Blob/DOM. Uses a plain object-URL + anchor click rather
 * than file-saver, whose CJS export does not survive webpack's dynamic-import
 * interop (the destructured `saveAs` comes back undefined).
 *
 * @returns {Promise<void>}
 */
export const downloadSourceDocx = async ({ event, config = {}, filename, withAnswers = false }) => {
  const docObj = buildSourceDocx({ event, config, withAnswers });
  const blob = await Packer.toBlob(docObj);
  const name = filename || sourceDocxFilename(event, config);

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  // Defer cleanup a tick — revoking the URL synchronously after click cancels
  // the download in some browsers.
  setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(url);
  }, 0);
};
